<script lang="ts">
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';

	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../../../helpers/interface_helpers';
	import { getErrorOrInfoMessageForMinAndMax } from '../../../../helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';

		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from '../../../../types/requests/settings/setting';

	import { Alert, Button, Input } from '@Shelly/shelly-design-system';

	export let device: Device;

	const id = real_id(device.id);
	const GAMMA_MIN = 0.4;
	const GAMMA_MAX = 4.0;
	const GAMMA_DEFAULT = 1.0;
	const GAMMA_STEP = 0.01;

	let settings = $settingsStore;
	let cacheGamma = settings?.[get_gen_2_component_channel_structure(device)]?.gamma ?? GAMMA_DEFAULT;
	let gamma: string = String(cacheGamma);
	let loading = false;

	$: settingsStore = getDeviceSettings(id);
	$: storeGamma = $settingsStore?.[get_gen_2_component_channel_structure(device)]?.gamma ?? GAMMA_DEFAULT;
	$: if (storeGamma !== cacheGamma && !loading) {
		gamma = String(storeGamma);
		cacheGamma = storeGamma;
	}
	$: gammaNum = gamma === '' ? NaN : Number(gamma);
	$: inputData = getErrorOrInfoMessageForMinAndMax(gammaNum, GAMMA_MIN, GAMMA_MAX);

	const setGamma = async () => {
		const num = gammaNum;
		if (cacheGamma === num || inputData.error || Number.isNaN(num)) return;
		loading = true;

		const params = {
			id: device.channel,
			config: { gamma: num },
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			params,
		);
		const response = onSettingsResponse(res, device);
		if (response && !response.error) {
			settings[get_gen_2_component_channel_structure(device)].gamma = num;
			devices_settings.update_devices_settings(id, settings);
			cacheGamma = num;
		} else {
			gamma = String(cacheGamma);
		}
		loading = false;
	};
</script>

<Alert
	class="mb-s"
	heading={$format('gamma_dimmer_description', {
		default:
			'Gamma correction adjusts the dimming curve for a more natural visual transition. Range 0.4–4.0; 1.0 means no correction. A value around 1.8 often gives the best visual result. This setting is independent from calibration.',
	})}
/>
<Input
	class="mb-s"
	size="m"
	bind:value={gamma}
	error={inputData.error}
	info={inputData.info}
	type="number"
	min={GAMMA_MIN}
	max={GAMMA_MAX}
	step={GAMMA_STEP}
	on:enter={setGamma}
	id="input-gamma-dimmer-g2"
	aria-label={$format('gamma_dimmer_description', {
		default: 'Gamma correction adjusts the dimming curve. Value between 0.4 and 4.0.',
	})}
/>
<Button {loading} size="m" width="full" on:click={setGamma}>
	{$format('button_save')}
</Button>
